import { useRef, useCallback } from 'react';
import { useVeloraStore } from '../store/useVeloraStore';
import { AudioCapture } from '../lib/audioCapture';
import { frequencyToNote } from '../lib/noteMap';

export function usePitch() {
  const store = useVeloraStore();
  const captureRef = useRef<AudioCapture | null>(null);

  const startPitch = useCallback(async () => {
    if (captureRef.current) return;
    captureRef.current = new AudioCapture(({ frequency, rms }) => {
      if (rms < 0.01 || frequency <= 0) {
        store.updatePitch(0, '--', 0, 0);
        return;
      }
      const info = frequencyToNote(frequency);
      store.updatePitch(frequency, info.fullName, info.cents, 1);
    });
    await captureRef.current.start();
  }, [store]);

  const stopPitch = useCallback(() => {
    captureRef.current?.stop();
    captureRef.current = null;
    store.updatePitch(0, '--', 0, 0);
  }, [store]);

  const {
    currentNote,
    currentFrequency,
    currentCents,
    stability,
    pitchHistory,
    score,
  } = store;

  const isInTune = currentNote !== '--' && Math.abs(currentCents) <= 15;
  const isFlat   = currentNote !== '--' && currentCents < -15;
  const isSharp  = currentNote !== '--' && currentCents > 15;

  return {
    note: currentNote,
    frequency: currentFrequency,
    cents: currentCents,
    stability,
    pitchHistory,
    score,
    isInTune,
    isFlat,
    isSharp,
    startPitch,
    stopPitch,
  };
}
